import { ApiProduct, Product } from "@/types/types"
import { isEmpty } from "lodash"
import { useEffect, useState } from "react"

// EAN có thể đến từ textarea (mỗi dòng 1 mã) hoặc rich-text editor (`<p>123</p><p>456</p>`).
// Thay tag HTML bằng khoảng trắng TRƯỚC khi tách, nếu không các mã sẽ dính liền thành một số.
function normalizeEans(eans: string[]): string[] {
  return eans
    .flatMap(ean => ean.replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ").split(/[\s,;]+/))
    .filter(Boolean)
}

const getProductsByEans = async (eans: string[]) => {
  try {
    const cleanedQuery = `eans=${normalizeEans(eans).join(",")}`

    const response = await fetch(`/api/products?${cleanedQuery}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(
        `Lỗi HTTP! Trạng thái: ${response.status}, Thông báo: ${errorData.message || "Lỗi không xác định"}`,
      )
    }

    const data = await response.json()
    return data
  } catch (error) {
    console.error("Lỗi khi tải sản phẩm:", error)
    return {
      error: true,
      message: error instanceof Error ? error.message : "Không thể tải danh sách sản phẩm",
      details: error,
    }
  }
}

function mapApiProductToProduct(apiProduct: ApiProduct): Product {
  const ean = apiProduct.product_features?.["582"]?.value || apiProduct.barcode_1
  const companyId = apiProduct.company_id
  const originPrice = parseFloat(apiProduct.list_price)
  const bundlePrice = parseFloat(apiProduct.price)
  const discount = originPrice > 0 ? (((originPrice - bundlePrice) / originPrice) * 100).toFixed(0) : "0"

  const now = Math.floor(Date.now() / 1000)
  const isLetak =
    apiProduct.letak_from_date !== "0" &&
    now >= parseInt(apiProduct.letak_from_date) &&
    now <= parseInt(apiProduct.letak_to_date)

  const isNew =
    apiProduct.new_label_from_date !== "0" &&
    now >= parseInt(apiProduct.new_label_from_date) &&
    now <= parseInt(apiProduct.new_label_to_date)

  const imageUrl = apiProduct.main_pair?.detailed?.https_image_path || ""
  const slug = apiProduct.product
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")

  return {
    id: apiProduct.product_id,
    name: apiProduct.product,
    ean: ean,
    companyId: companyId,
    originPrice: apiProduct.list_price,
    nonBundlePrice: apiProduct.list_price,
    bundlePrice: apiProduct.price,
    bundleSize: parseInt(apiProduct.unit_per_box),
    qf: false,
    expiryDate: null,
    inStockCount: parseInt(apiProduct.amount),
    isLetak: isLetak,
    discountPercentage: discount,
    isSpecial: parseFloat(discount) > 0,
    isNew: isNew,
    eanMask: ean.slice(-4),
    image: {
      origin: imageUrl,
      md: imageUrl,
      sm: imageUrl,
      lg: imageUrl,
      xsm: imageUrl,
    },
    slug: slug,
    url: apiProduct.url,
  }
}

// Chi nhánh tamdaexpress (companyId trên CS-Cart).
const COMPANY_TDE_FRESH_PRAHA = "3"
const COMPANY_TDE_DRY_PRAHA = "4"

// Chỉ hiển thị sản phẩm thuộc các chi nhánh này.
const ALLOWED_COMPANY_IDS = new Set([COMPANY_TDE_FRESH_PRAHA, COMPANY_TDE_DRY_PRAHA])

// tamdaexpress trả về mỗi vendor (companyId) là một bản ghi riêng. Chỉ giữ lại
// sản phẩm thuộc chi nhánh Tde fresh/dry Praha, giữ nguyên thứ tự gốc.
function filterProductsByAllowedBranch(products: Product[]): Product[] {
  return products.filter(product => ALLOWED_COMPANY_IDS.has(product.companyId))
}

export const useProductsByEans = (eans: string[] | undefined) => {
  const [products, setProducts] = useState<Product[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    async function init() {
      if (eans && eans.length > 0) {
        setLoading(true)
        setError(null)
        try {
          const result = await getProductsByEans(eans)

          if (result?.error) {
            console.error("❌ Lỗi trong kết quả trả về:", result.message)
            setError(result.message || "Không thể tải danh sách sản phẩm")
            setProducts([])
          } else if (result?.products && Array.isArray(result.products)) {
            const mapped: Product[] = result.products.map(mapApiProductToProduct)
            const products = filterProductsByAllowedBranch(mapped)
            setProducts(products)
          } else {
            console.warn("⚠️ Định dạng kết quả không mong đợi:", result)
            setProducts([])
          }
        } catch (err) {
          console.error("❌ Lỗi trong useProductsByEans:", err)
          setError("Đã xảy ra lỗi không mong đợi")
          setProducts([])
        } finally {
          setLoading(false)
        }
      }
    }

    if (!isEmpty(eans)) init()
  }, [eans])

  return { products, loading, error }
}
